import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, MotionConfig } from "motion/react";
import { ProductSelectionStep } from "./components/ProductSelectionStep";
import { ProductViewerStep } from "./components/ProductViewerStep";
import { VariantSelectionStep } from "./components/VariantSelectionStep";
import { SheetCloseButton } from "./components/SheetCloseButton";
import { familyTrayHeightStyle, useFamilyTrayHeights } from "./useFamilyTrayHeights";

type TrayView = "PRODUCT_SELECTION" | "PRODUCT_DETAILS" | "VARIANT_SELECTION";

const FamilyTray = ({
  open,
  onOpenChange,
  couponName,
  onAddSelected,
  isSubmitting = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  couponName?: string;
  /** Footer CTA — add selected items to cart + apply the discount. Falls back to closing the tray. */
  onAddSelected?: () => void;
  isSubmitting?: boolean;
}) => {
  const [view, setView] = useState<TrayView>("PRODUCT_SELECTION");
  // Where the variant selector was opened from — back returns there (grid vs product viewer)
  const [variantOrigin, setVariantOrigin] = useState<TrayView>("PRODUCT_SELECTION");
  // Portal root is rendered by React (index.tsx), so it only exists in the DOM after mount —
  // must look it up in an effect, not during render
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setContainer(document.getElementById("checkout-ui-root"));
  }, []);

  const trayHeights = useFamilyTrayHeights(container);

  const openVariantsFrom = (origin: TrayView) => {
    setVariantOrigin(origin);
    setView("VARIANT_SELECTION");
  };

  const renderContent = () => {
    switch (view) {
      case "VARIANT_SELECTION":
        return <VariantSelectionStep onBack={() => setView(variantOrigin)} />;
      case "PRODUCT_DETAILS":
        return (
          <ProductViewerStep
            onBack={() => setView("PRODUCT_SELECTION")}
            onOpenVariants={() => openVariantsFrom("PRODUCT_DETAILS")}
            onAdd={() => {
              setView("PRODUCT_SELECTION");
            }}
          />
        );
      case "PRODUCT_SELECTION":
      default:
        return (
          <ProductSelectionStep
            couponName={couponName}
            isSubmitting={isSubmitting}
            onIncrement={() => {}}
            onViewProduct={() => setView("PRODUCT_DETAILS")}
            onOpenVariants={() => openVariantsFrom("PRODUCT_SELECTION")}
            onNext={() => (onAddSelected ? onAddSelected() : onOpenChange(false))}
          />
        );
    }
  };

  if (!container) return null;

  return createPortal(
    <AnimatePresence
      onExitComplete={() => {
        // Runs once the close animation finishes — replaces the old reset timeout
        setView("PRODUCT_SELECTION");
      }}>
      {open && (
        /* Radix modal (Coupons dialog) sets pointer-events:none on body while open —
           re-enable here since this tray portals outside the Radix content */
        /* `antialiased` is scoped here rather than globally — font smoothing is inherited, so
           every tray descendant picks it up without touching the rest of checkout-ui */
        <div
          className="pointer-events-auto absolute inset-0 z-50 antialiased"
          style={familyTrayHeightStyle(trayHeights)}>
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <MotionConfig transition={{ type: "spring", bounce: 0, duration: 0.45 }}>
            {/* Slide-up shell — handles ONLY the y transform.
                No `layout` here so FLIP never fights the entrance/exit transform. */}
            <motion.div
              role="dialog"
              aria-modal="true"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.5 }}
              className="absolute bottom-0 inset-x-0 w-full">
              {/* Lives on the shell, NOT the layer below — that one is overflow-hidden and would
                  clip a `bottom-full` child. Shell height tracks the active view, so the button
                  rides the FLIP morph and the slide-up/down transform for free. */}
              <SheetCloseButton onClose={() => onOpenChange(false)} />
              {/* Height-animating layer — Motion measures this via FLIP.
                  overflow-hidden + r
                  adius live HERE because this is the
                  element whose height actually changes. */}
              <motion.div
                layout
                className="relative overflow-hidden rounded-t-[28px] bg-neutral-50 shadow-2xl">
                <AnimatePresence initial={false} mode="popLayout">
                  <motion.div
                    key={view}
                    layout="position"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}>
                    {renderContent()}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </MotionConfig>
        </div>
      )}
    </AnimatePresence>,
    container,
  );
};

export default FamilyTray;
